import { forceSessionExpiredLogout } from './sessionExpiry';

// Shares one in-progress refresh among requests from this JavaScript context (one tab).
let refreshPromise = null;
// Web Locks are origin-scoped, so this name serializes refreshes across all portal tabs.
const REFRESH_LOCK_NAME = 'frontdoor-access-token-refresh';

// The server definitively rejected the refresh token (or the session has none) — the
// session is over. Anything else (network failure, 5xx) is transient and must NOT
// end the session: the next request will simply try to refresh again.
export class RefreshTokenRejectedError extends Error {
    constructor(message, status) {
        super(message);
        this.name = 'RefreshTokenRejectedError';
        this.status = status;
    }
}

function getStoredAuth() {
    try {
        return JSON.parse(localStorage.getItem(process.env.REACT_APP_JWT_KEY));
    } catch {
        return null;
    }
}

function updateStoredTokens(token, refreshToken) {
    const auth = getStoredAuth();
    if (auth) {
        localStorage.setItem(
            process.env.REACT_APP_JWT_KEY,
            JSON.stringify({ ...auth, token, refreshToken }),
        );
    }
}

// Transient refresh failures (network error, 5xx) are retried inside the cross-tab lock —
// long enough to ride out an API deploy blip or a wake-from-sleep network race, short
// enough not to hang a user-facing request. Total attempts = delays + 1.
const REFRESH_RETRY_DELAYS_MS = [500, 1500];

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function requestNewTokens(refreshToken) {
    for (let attempt = 0; ; attempt++) {
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + '/apiv2/auth/refresh', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken }),
            });

            if ([400, 401, 403].includes(response.status)) {
                throw new RefreshTokenRejectedError(
                    `Refresh rejected (${response.status})`,
                    response.status,
                );
            }

            if (!response.ok) throw new Error(`Refresh failed (${response.status})`);

            return await response.json();
        } catch (error) {
            if (error instanceof RefreshTokenRejectedError) throw error;
            if (attempt >= REFRESH_RETRY_DELAYS_MS.length) throw error;
            await delay(REFRESH_RETRY_DELAYS_MS[attempt]);
        }
    }
}

async function performRefresh(accessToken) {
    const auth = getStoredAuth();

    // Re-read storage only after acquiring the cross-tab lock. If the access token has
    // changed since the failed request was sent, a previous lock holder already refreshed.
    // Skip a duplicate refresh; fetchExpresso will retry with the current stored token.
    if (auth?.token && auth.token !== accessToken) return null;

    if (!auth?.refreshToken) throw new RefreshTokenRejectedError('No refresh token');

    const data = await requestNewTokens(auth.refreshToken);
    updateStoredTokens(data.token, data.refreshToken);
    return data;
}

function refreshAccessToken(accessToken) {
    // Same-tab callers share the same refresh request
    if (refreshPromise) return refreshPromise;

    const refreshWithLockIfAvailable = (async () => {
        try {
            if (typeof navigator !== 'undefined' && navigator.locks) {
                // Only one tab on this origin can enter performRefresh at a time.
                // Waiting tabs re-check localStorage after they acquire the lock.
                return await navigator.locks.request(REFRESH_LOCK_NAME, { mode: 'exclusive' }, () =>
                    performRefresh(accessToken),
                );
            }

            // Web Locks is unavailable: retain the same-tab de-duplication above.
            return await performRefresh(accessToken);
        } catch (error) {
            if (error instanceof RefreshTokenRejectedError) {
                forceSessionExpiredLogout();
            }
            throw error;
        }
    })();

    refreshPromise = refreshWithLockIfAvailable.then(
        (value) => {
            refreshPromise = null;
            return value;
        },
        (error) => {
            refreshPromise = null;
            throw error;
        },
    );

    return refreshPromise;
}

// Refresh ahead of expiry so requests almost never go out with a dead token. The buffer
// matches the dashboard poll interval (5 min), so a polling screen always renews in time.
const TOKEN_EXPIRY_BUFFER_MS = 5 * 60_000;

// Reads the exp claim of a JWT. Returns null for anything undecodable, in which case the
// proactive check is skipped and the reactive 401 path is the safety net.
function getTokenExpiryMs(token) {
    try {
        const { exp } = JSON.parse(atob(token.split('.')[1]));
        return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
        return null;
    }
}

async function refreshProactivelyIfExpiring() {
    const auth = getStoredAuth();
    if (!auth?.token || !auth.refreshToken) return;

    const expiryMs = getTokenExpiryMs(auth.token);
    if (expiryMs === null || expiryMs - Date.now() >= TOKEN_EXPIRY_BUFFER_MS) return;

    try {
        await refreshAccessToken(auth.token);
    } catch {
        // Transient failure: the current token is still valid inside the buffer window, so
        // send the request with it — the reactive 401 path remains the fallback.
        // Definitive failure: the session-expired redirect is already under way.
    }
}

function buildFetchOptions(options) {
    let fetchOptions = {};

    if (options) {
        const headers = options.headers || null;
        const body = options.body || undefined;

        fetchOptions = {
            method: options.method,
            headers: {
                'Content-Type': 'application/json',
                ...headers,
            },
            body: JSON.stringify(body),
        };
    } else {
        fetchOptions = {
            method: 'GET',
            headers: {},
        };
    }

    const auth = getStoredAuth();
    if (auth) {
        fetchOptions.headers['x-access-token'] = auth.token;
    }

    return fetchOptions;
}

export default async function fetchExpresso(endpoint, options) {
    const url = process.env.REACT_APP_API_URL + endpoint;

    await refreshProactivelyIfExpiring();

    let fetchOptions = buildFetchOptions(options);

    // Preserve the token used for this request so the lock holder can tell whether
    // another tab refreshed the shared session while this request was in flight.
    const accessToken = fetchOptions.headers['x-access-token'];

    let response = await fetch(url, fetchOptions);

    if (response.status === 401) {
        const auth = getStoredAuth();

        if (auth?.refreshToken) {
            try {
                await refreshAccessToken(accessToken);
            } catch {
                // Refresh failed — hand the original 401 back to the caller. On a transient
                // failure the session stays in storage so a later request can retry.
                return response;
            }

            // Whether this tab refreshed or another tab already did, retry once using
            // the access token currently stored in localStorage.
            fetchOptions = buildFetchOptions(options);
            response = await fetch(url, fetchOptions);
        } else if (auth) {
            // Legacy session without refresh token — force re-login
            forceSessionExpiredLogout();
        } else if (accessToken) {
            // The request went out authenticated but storage is now empty: another tab
            // already ended the session — converge this tab too. Requests that never
            // carried a token (login, forgot password) fall through: their 401 belongs
            // to the caller.
            forceSessionExpiredLogout();
        }
    }

    return response;
}
